/** Корневой компонент: определяет вошедшего, выбранный сервер и страницу. */

import { useEffect, useState, type ReactNode } from 'react';
import { ALLOWED_INVITER_IDS, hasCapability, type AccessLevel, type Capability } from '@ak/shared';

import { apiGet } from './lib/api.ts';
import { resolveShell, type MeResponse } from './lib/guilds.ts';
import { setLiveUpdates } from './lib/live.ts';
import { matchRoute, navigate, useRoute, type RouteMatch } from './lib/router.ts';
import { DEFAULT_SETTINGS_TAB } from './lib/settings-tabs.ts';
import { Layout, PlainShell } from './components/layout.tsx';
import { Spinner } from './components/ui.tsx';
import { ApplicationsPage } from './pages/Applications.tsx';
import { AuditPage } from './pages/Audit.tsx';
import { BroadcastPage } from './pages/Broadcast.tsx';
import { EventDetailPage } from './pages/EventDetail.tsx';
import { EventsPage } from './pages/Events.tsx';
import { GuildPickerPage } from './pages/GuildPicker.tsx';
import { LockedPage } from './pages/Locked.tsx';
import { LoginPage } from './pages/Login.tsx';
import { OverviewPage } from './pages/Overview.tsx';
import { PlayerCardPage } from './pages/PlayerCard.tsx';
import { PlayersPage } from './pages/Players.tsx';
import { RollbacksPage } from './pages/Rollbacks.tsx';
import { CaptureDetailPage } from './pages/CaptureDetail.tsx';
import { StaticCardPage } from './pages/StaticCard.tsx';
import { StatsPage } from './pages/Stats.tsx';
import { TacticsPage } from './pages/Tactics.tsx';
import { SettingsPage } from './pages/settings/index.tsx';
import { SetupPage } from './pages/Setup.tsx';

/**
 * Право, без которого раздел не открывается.
 * Разделы без записи здесь доступны всем, кто вообще вошёл.
 */
const PAGE_CAPABILITY: Partial<Record<RouteMatch['page'], Capability>> = {
  overview: 'events.view',
  events: 'events.view',
  event: 'events.view',
  players: 'players.view',
  // Карточка игрока — часть статистики: на неё ведут ссылки из таблицы и
  // из ленты боёв, и без неё «подробно по каждому капту» рассыпается.
  player: 'stats.view',
  applications: 'applications.review',
  rollbacks: 'rollbacks.view',
  stats: 'stats.view',
  capture: 'stats.view',
  static: 'stats.view',
  tactics: 'tactics.manage',
  broadcast: 'events.manage',
  audit: 'audit.view',
  settings: 'settings.edit',
  setup: 'settings.edit',
};

/** Названия разделов — те же, что в меню. */
const PAGE_TITLE: Partial<Record<RouteMatch['page'], string>> = {
  overview: 'Обзор',
  events: 'Наборы',
  event: 'Набор',
  players: 'Игроки',
  player: 'Игрок',
  applications: 'Заявки',
  rollbacks: 'Откаты',
  stats: 'Статистика',
  capture: 'Капт',
  static: 'Игрок без анкеты',
  tactics: 'Дефолты',
  broadcast: 'Рассылка',
  audit: 'Журнал',
  settings: 'Настройки',
  setup: 'Разметка сервера',
};

const LEVELS: readonly AccessLevel[] = ['player', 'moderator', 'admin', 'owner'];

export function App(): ReactNode {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const path = useRoute();

  useEffect(() => {
    apiGet<MeResponse>('/api/me')
      .then(setMe)
      .catch(() => {
        setFailed(true);
      });
  }, []);

  const level = me?.user?.level ?? null;

  /*
   * Живой поток — только тем, кто управляет. Игроку он не нужен: цифры
   * меняются раз в вечер, а соединение висит у каждого зрителя.
   */
  useEffect(() => {
    setLiveUpdates(level === null || hasCapability(level, 'events.view'));
  }, [level]);

  /*
   * Игрока с корня уводим в статистику: «Обзор» ему закрыт, и первое, что
   * он увидел бы, — замок вместо того, ради чего пришёл по кнопке из
   * Discord.
   */
  useEffect(() => {
    if (level === null || path !== '/') {
      return;
    }

    if (!hasCapability(level, 'events.view') && hasCapability(level, 'stats.view')) {
      navigate('/stats');
    }
  }, [level, path]);

  if (failed) {
    return (
      <div className="grid min-h-screen place-items-center p-6 text-center">
        <div>
          <p className="font-medium">Сервер не отвечает</p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            Проверьте, запущен ли бот, и обновите страницу.
          </p>
        </div>
      </div>
    );
  }

  if (me === null) {
    return (
      <div className="grid min-h-screen place-items-center" style={{ color: 'var(--text-muted)' }}>
        <Spinner size={28} />
      </div>
    );
  }

  const shell = resolveShell(me);
  const canInvite = me.user !== null && ALLOWED_INVITER_IDS.includes(me.user.userId);
  const inviteUrl: string | null = canInvite ? me.bot.inviteUrl : null;
  const botAvatarUrl = me.bot.avatarUrl;

  if (shell.kind === 'login') {
    return (
      <LoginPage
        oauthConfigured={me.auth.oauthConfigured}
        devLoginEnabled={me.auth.devLoginEnabled}
        botAvatarUrl={botAvatarUrl}
      />
    );
  }

  if (shell.kind === 'picker') {
    return <GuildPickerPage user={shell.user} inviteUrl={inviteUrl} />;
  }

  // Неразмеченный сервер: разделы админки показывать нечего, пока на сервере
  // нет ни ролей, ни каналов.
  if (shell.kind === 'setup') {
    return (
      <PlainShell guild={shell.guild} inviteUrl={inviteUrl}>
        <SetupPage user={shell.user} guild={shell.guild} />
      </PlainShell>
    );
  }

  const route = matchRoute(path);
  const { timezone, eventTypes, defaultCapacity } = shell.settings;
  const user = shell.user;

  // Закрытый раздел не исчезает из меню, а открывает экран с замком:
  // так видно, что раздел есть, просто прав на него не хватает.
  const required = PAGE_CAPABILITY[route.page];
  if (required !== undefined && !hasCapability(user.level, required)) {
    return (
      <Layout user={user} guild={shell.guild} inviteUrl={inviteUrl} botAvatarUrl={botAvatarUrl} currentPath={path}>
        <LockedPage
          title={PAGE_TITLE[route.page] ?? 'Раздел'}
          level={user.level}
          allowedLevels={LEVELS.filter((level) => hasCapability(level, required))}
        />
      </Layout>
    );
  }

  const page = ((): ReactNode => {
    switch (route.page) {
      case 'overview':
        return <OverviewPage timezone={timezone} />;
      case 'events':
        return (
          <EventsPage user={user} timezone={timezone} eventTypes={eventTypes} defaultCapacity={defaultCapacity} />
        );
      case 'event':
        return route.eventId === undefined ? null : (
          <EventDetailPage eventId={route.eventId} user={user} timezone={timezone} />
        );
      case 'players':
        return <PlayersPage user={user} timezone={timezone} />;
      case 'player':
        return route.userId === undefined ? null : <PlayerCardPage userId={route.userId} timezone={timezone} />;
      case 'applications':
        return <ApplicationsPage timezone={timezone} />;
      case 'rollbacks':
        return <RollbacksPage timezone={timezone} />;
      case 'stats':
        return <StatsPage user={user} timezone={timezone} />;
      case 'tactics':
        return <TacticsPage timezone={timezone} />;
      case 'capture':
        return route.captureId === undefined ? null : (
          <CaptureDetailPage captureId={route.captureId} timezone={timezone} />
        );
      case 'static':
        return route.staticId === undefined ? null : (
          <StaticCardPage staticId={route.staticId} timezone={timezone} />
        );
      case 'broadcast':
        return <BroadcastPage />;
      case 'audit':
        return <AuditPage timezone={timezone} />;
      case 'settings':
        return <SettingsPage tab={route.settingsTab ?? DEFAULT_SETTINGS_TAB} />;
      case 'setup':
        return <SetupPage user={user} guild={shell.guild} />;
      case 'unknown':
        return (
          <div className="py-20 text-center">
            <p className="text-lg font-medium">Страница не найдена</p>
            <button
              className="mt-4 text-sm underline"
              style={{ color: 'var(--accent)' }}
              onClick={() => {
                navigate('/');
              }}
            >
              Вернуться на обзор
            </button>
          </div>
        );
    }
  })();

  return (
    <Layout
      user={user}
      guild={shell.guild}
      inviteUrl={inviteUrl}
      botAvatarUrl={botAvatarUrl}
      currentPath={path}
    >
      {page}
    </Layout>
  );
}
